/**
 * The health intake's order, copy and choice lists (specs/product/intake.md). Pregnancy is skipped
 * when sex is male (INT-005). Disqualifiers wait on Andrea (Q-012), so every answer continues.
 */
import type { IntakeAnswers, QuestionId } from "@lattice/core";

const ORDER: readonly QuestionId[] = [
  "date_of_birth",
  "sex",
  "pregnancy",
  "height_weight",
  "conditions",
  "medications",
  "supplements",
  "allergies",
  "diet",
  "exercise",
  "goal",
];

export function visibleQuestions(answers: IntakeAnswers): readonly QuestionId[] {
  return ORDER.filter((id) => id !== "pregnancy" || answers.sex === "female");
}

export function intakeComplete(answers: IntakeAnswers): boolean {
  const vis = visibleQuestions(answers);

  return vis.length > 0 && vis.every((id) => answers[id] !== undefined);
}

export interface Prompt {
  readonly heading: string;
  readonly lead: string;
}

export const PROMPT: Readonly<Record<QuestionId, Prompt>> = {
  date_of_birth: {
    heading: "When were you born?",
    lead: "Doses and lab ranges depend on age.",
  },
  sex: {
    heading: "What is your sex?",
    lead: "Lab ranges and some doses differ.",
  },
  pregnancy: {
    heading: "Pregnant, breastfeeding or trying to conceive?",
    lead: "This changes what a physician can sign.",
  },
  height_weight: {
    heading: "How tall are you, and your weight?",
    lead: "Protein dose is based on your weight.",
  },
  conditions: {
    heading: "Any conditions we should know?",
    lead: "Kidney disease and others change what is safe.",
  },
  medications: {
    heading: "Any prescription medications?",
    lead: "Include GLP-1s such as Ozempic or Mounjaro.",
  },
  supplements: {
    heading: "Any supplements you take now?",
    lead: "We will not double up on what you already take.",
  },
  allergies: {
    heading: "Any allergies or intolerances?",
    lead: "This picks your protein.",
  },
  diet: {
    heading: "How do you eat?",
    lead: "This picks your protein.",
  },
  exercise: {
    heading: "How do you train?",
    lead: "Days each week, and the main kind of work.",
  },
  goal: {
    heading: "What do you want this formula for?",
    lead: "This changes which extras go in.",
  },
};

export const SEX: readonly { readonly value: "female" | "male"; readonly label: string }[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
];

export const DIET: readonly {
  readonly value: NonNullable<IntakeAnswers["diet"]>;
  readonly label: string;
}[] = [
  { value: "omnivore", label: "I eat meat and plants" },
  { value: "pescatarian", label: "Fish, no other meat" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "other", label: "Something else" },
];

export const GOAL: readonly {
  readonly value: NonNullable<IntakeAnswers["goal"]>;
  readonly label: string;
}[] = [
  { value: "general", label: "Stay well" },
  { value: "energy", label: "More energy" },
  { value: "strength", label: "Strength and muscle" },
  { value: "weight", label: "Weight management" },
  { value: "other", label: "Something else" },
];

export const EXERCISE_TYPE: readonly {
  readonly value: NonNullable<IntakeAnswers["exercise"]>["type"];
  readonly label: string;
}[] = [
  { value: "strength", label: "Strength" },
  { value: "endurance", label: "Endurance" },
  { value: "mixed", label: "Both" },
  { value: "other", label: "Something else" },
];

export const CONDITIONS = [
  "Kidney disease",
  "Type 2 diabetes",
  "Thyroid disease",
  "Anemia",
  "High blood pressure",
  "PCOS",
  "IBD or Crohn's",
];

export const MEDICATIONS = [
  "Semaglutide (Ozempic, Wegovy)",
  "Tirzepatide (Mounjaro, Zepbound)",
  "Levothyroxine",
  "Metformin",
  "A statin",
  "An SSRI",
];

export const SUPPLEMENTS = [
  "Vitamin D",
  "Omega-3",
  "Magnesium",
  "Creatine",
  "A multivitamin",
  "Iron",
];

export const ALLERGIES = ["Dairy", "Soy", "Gluten", "Eggs", "Tree nuts", "Peanuts", "Shellfish"];

function listed(values: readonly string[]): string {
  return values.length === 0 ? "None" : values.join(", ");
}

function named(
  options: readonly { readonly value: string; readonly label: string }[],
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function exerciseLine(answers: IntakeAnswers): string {
  const exercise = answers.exercise;

  if (exercise === undefined) return "Not answered";

  if (exercise.daysPerWeek === 0 || exercise.type === "none") return "None";

  return `${String(exercise.daysPerWeek)} days, ${named(EXERCISE_TYPE, exercise.type)}`;
}

function bodyLine(answers: IntakeAnswers): string {
  const body = answers.height_weight;

  return body === undefined ? "Not answered" : `${body.heightCm} cm, ${body.weightKg} kg`;
}

function chips(value: readonly string[] | undefined): string {
  return value === undefined ? "Not answered" : listed(value);
}

const BORN = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** The stored day, written the way a person writes a birthday. A value that is not a date stays as it was. */
function birthday(stored: string): string {
  const date = new Date(`${stored}T00:00:00Z`);

  return Number.isNaN(date.getTime()) ? stored : BORN.format(date);
}

/** The answer as it should read on the recap, not the stored JSON. */
export function answerLine(id: QuestionId, answers: IntakeAnswers): string {
  switch (id) {
    case "date_of_birth":
      return answers.date_of_birth === undefined
        ? "Not answered"
        : birthday(answers.date_of_birth.date);
    case "sex":
      return answers.sex === undefined ? "Not answered" : named(SEX, answers.sex);
    case "pregnancy":
      if (answers.pregnancy === undefined) return "Not answered";

      return answers.pregnancy ? "Yes" : "No";
    case "height_weight":
      return bodyLine(answers);
    case "conditions":
      return chips(answers.conditions);
    case "medications":
      return chips(answers.medications);
    case "supplements":
      return chips(answers.supplements?.map((item) => item.name));
    case "allergies":
      return chips(answers.allergies);
    case "diet":
      return answers.diet === undefined ? "Not answered" : named(DIET, answers.diet);
    case "exercise":
      return exerciseLine(answers);
    case "goal":
      return answers.goal === undefined ? "Not answered" : named(GOAL, answers.goal);
  }
}
